import type { AppId } from '@omnia/shared';

export const LOCALE = 'tr-TR';

export const tr = {
  brand: 'Omnia',
  brandFull: 'Omnia by Çankayapı',
  desk: 'Masaüstü',
  apps: {
    files: 'Dosyalarım',
    notes: 'Notlar',
    calculator: 'Hesap Makinesi',
    calendar: 'Takvim',
    kasa: 'Kasa',
    pdf: 'PDF Görüntüleyici',
    image: 'Görüntüleyici',
    docs: 'Belgeler',
    sheets: 'Tablolar',
    cad: 'Plan — Çizim',
    video: 'Video Editör',
    ai: 'AI Asistan',
    notebook: 'Serbest El Defteri',
    browser: 'Tarayıcı',
  } satisfies Record<AppId, string>,
  common: {
    cancel: 'Vazgeç',
    delete: 'Sil',
    retry: 'Tekrar dene',
    loading: 'Yükleniyor…',
    offline: 'Sunucuya ulaşılamıyor. Bağlantınızı kontrol edin.',
    unknownError: 'Beklenmeyen bir hata oluştu.',
  },
  auth: {
    loginTitle: 'Masaüstünüze giriş yapın',
    registerTitle: 'Yeni masaüstü oluşturun',
    tagline: 'Dosyalarınız, notlarınız ve takviminiz — her cihazda aynı masada.',
    name: 'Ad soyad',
    email: 'E-posta',
    password: 'Şifre',
    passwordHint: 'En az 8 karakter',
    login: 'Giriş yap',
    register: 'Hesap oluştur',
    toRegister: 'Hesabınız yok mu? Oluşturun',
    toLogin: 'Zaten hesabınız var mı? Giriş yapın',
    welcome: (name: string) => `Hoş geldin, ${name}`,
    logout: 'Çıkış yap',
  },
  window: {
    minimize: 'Küçült',
    maximize: 'Büyüt',
    restore: 'Önceki boyuta döndür',
    close: 'Kapat',
    back: 'Masaüstü',
    unsavedTitle: 'Kaydedilmemiş değişiklikler var',
    discardCopy: {
      close: {
        body: 'Pencereyi kapatırsanız son kaydedilmeyen değişiklikler kaybolur.',
        action: 'Kaydetmeden kapat',
      },
      replace: {
        body: 'Başka bir dosya açarsanız bu penceredeki kaydedilmemiş değişiklikler kaybolur.',
        action: 'Kaydetmeden aç',
      },
      logout: {
        body: 'Çıkış yaparsanız açık pencerelerdeki kaydedilmemiş değişiklikler kaybolur.',
        action: 'Kaydetmeden çıkış yap',
      },
    },
  },
  settings: {
    title: 'Ayarlar',
    open: 'Ayarlar',
    hint: 'Ctrl+,',
    close: 'Ayarları kapat',
    back: 'Ayarlar listesine dön',
    tabList: 'Ayar bölümleri',
    loading: 'Yükleniyor…',
    tabs: {
      display: 'Görüntü',
      infobar: 'Bilgi çubuğu',
      locations: 'Konumlar',
      about: 'Hakkında',
    },
    theme: 'Tema',
    barSection: 'Üst çubuk',
    clockSection: 'Saat ve tarih',
    showDate: 'Tarihi göster',
    wallpaperSection: 'Duvar kağıdı',
    wallpaperNone: 'Yok',
    wallpaperFilter: 'Duvar kağıdı tonu',
    wallpaperAll: 'Tümü',
    wallpaperDark: 'Koyu',
    wallpaperLight: 'Açık',
    wallpaperHint: 'Görüntüler ekran boyutuna göre otomatik sığdırılır.',
    resetSection: 'Sıfırla',
    resetHint: 'Bilgi çubuğu, şehirler ve görünüm tercihleri varsayılana döner.',
    version: (value: string) => `Sürüm ${value}`,
    aboutBody: 'Tarayıcıda çalışan masaüstü: dosyalarınız, belgeleriniz, notlarınız ve hesaplarınız her cihazda aynı masada.',
  },
  startMenu: {
    open: 'Omnia menüsü',
    hint: 'Ctrl+Alt+O',
    apps: 'Uygulamalar',
    search: 'Uygulama ara…',
    searchLabel: 'Uygulamalarda ara',
    noMatch: 'Uygulama bulunamadı.',
    view: 'Görünüm',
  },
  menu: {
    view: 'Görünüm',
    theme: 'Tema',
    themeSystem: 'Sistemle aynı',
    themeLight: 'Açık',
    themeDark: 'Koyu',
    arrange: 'Pencereleri düzenle',
    minimizeAll: 'Tümünü küçült',
    infobar: 'Bilgi çubuğu',
    infobarShow: 'Göster',
    infobarShowHint: 'Ctrl+Alt+B',
    infobarAbove: 'Üstte kalsın',
    infobarBehind: 'Arkada kalsın',
  },
  infobar: {
    label: 'Bilgi çubuğu',
    settings: 'Ayarlar',
    show: 'Bilgi çubuğunu göster',
    hide: 'Bilgi çubuğunu gizle',
    toggleHint: 'Ctrl+Alt+B',
    expand: 'Bilgi çubuğunu aç',
    close: 'Kapat',
    widgets: 'Bileşenler',
    position: 'Çubuk konumu',
    reset: 'Varsayılana dön',
    sectionAppearance: 'Görünüm',
    sectionBar: 'Bilgi çubuğu',
    sectionLocations: 'Konumlar',
    sectionClock: 'Saat',
    theme: 'Tema',
    showBar: 'Bilgi çubuğunu göster',
    showSeconds: 'Saniyeyi göster',
    savedCities: 'Kayıtlı şehirler',
    primaryCity: 'Ana şehir',
    setPrimary: (name: string) => `${name} şehrini ana şehir yap`,
    removeCity: (name: string) => `${name} şehrini kaldır`,
    moveCityUp: (name: string) => `${name} şehrini yukarı taşı`,
    moveCityDown: (name: string) => `${name} şehrini aşağı taşı`,
    addCity: 'Şehir ekle',
    addCityMenu: 'Konum ekle…',
    cityLimit: (max: number) => `En fazla ${max} şehir eklenebilir.`,
    cityChip: (name: string, details: string) => `${name}: ${details}`,
    showCity: (name: string) => `${name} şehrini göster`,
    currentCity: 'Seçili şehir',
    marketsSection: 'Piyasalar',
    widgetsSection: 'Bileşenler',
    positionAbove: 'Üstte kalsın',
    positionAboveHint: 'Pencereler çubuğun altında açılır.',
    positionBehind: 'Arkada kalsın',
    positionBehindHint: 'Pencereler çubuğun üzerine sürüklenebilir.',
    markets: 'Piyasalar',
    location: 'Konum',
    changeLocation: 'Konumu değiştir',
    locationSearch: 'Şehir ara…',
    locationSearchLabel: 'Şehir ara',
    locationEmpty: 'Sonuç bulunamadı.',
    locationError: 'Arama şu an yapılamıyor.',
    searching: 'Aranıyor…',
    widget: {
      weather: 'Hava durumu',
      markets: 'Piyasalar',
      netWorth: 'Net varlık',
      bell: 'Bildirimler',
    },
    market: {
      usd: 'ABD Doları',
      eur: 'Euro',
      gold: 'Gram altın',
      btc: 'Bitcoin',
      ceyrek: 'Çeyrek altın',
      yarim: 'Yarım altın',
      tam: 'Tam altın',
      cumhuriyet: 'Cumhuriyet altını',
      ayar22: '22 ayar altın (gr)',
    },
    marketsHint: 'Dar ekranlarda bazı göstergeler otomatik gizlenir.',
    source: (source: string, asOf?: string) => (asOf ? `${source}, ${asOf}` : source),
    weatherUnavailable: 'Hava durumu alınamadı',
    marketsUnavailable: 'Piyasa verisi alınamadı',
    loadingShort: '…',
    stale: 'Güncel değil — son bilinen değer',
    wind: (kmh: number) => `${Math.round(kmh)} km/sa`,
    windLabel: 'Rüzgâr',
    noRain: 'Yağış yok',
    rainNow: (mm: string) => `Yağış: ${mm} mm`,
    rainChance: (percent: number) => `Yağış ihtimali %${percent}`,
    forecastAt: (hour: string, temp: string, label: string) => `${hour}: ${temp}, ${label}`,
    up: 'yükseliyor',
    down: 'düşüyor',
    flat: 'değişmedi',
    change24h: 'Önceki kapanışa göre değişim',
    notifications: 'Bildirimler',
    noNotifications: 'Bildirim yok',
    unread: (count: number) => `${count} okunmamış bildirim`,
    markAllRead: 'Tümünü okundu say',
    details: 'Bilgi çubuğunu genişlet',
    netWorth: {
      open: 'Kasa uygulamasını aç: net varlık',
      reveal: 'Net varlığı göster',
      conceal: 'Net varlığı gizle',
      masked: 'Net varlık gizli',
      changeToday: 'Dünkü kayda göre değişim',
    },
  },
  dock: { label: 'Uygulamalar', running: 'açık' },
  notes: {
    placeholder: "Yeni görev ekle, Enter'a bas…",
    addLabel: 'Yeni görev',
    summary: (open: number, done: number) => `${open} açık · ${done} tamamlandı`,
    empty: 'Liste boş. Aklındaki ilk işi yaz, Enter ile ekle.',
    markDone: (text: string) => `“${text}” tamamlandı olarak işaretle`,
    remove: (text: string) => `“${text}” görevini sil`,
    clearDone: 'Tamamlananları temizle',
  },
  calculator: {
    clear: 'Temizle',
    allClear: 'Tümünü temizle',
    negate: 'İşareti değiştir',
    percent: 'Yüzde',
    divide: 'Böl',
    multiply: 'Çarp',
    subtract: 'Çıkar',
    add: 'Topla',
    equals: 'Eşittir',
    decimal: 'Ondalık ayırıcı',
    error: 'Tanımsız',
    backspace: 'Son rakamı sil',
    modeLabel: 'Hesap makinesi türü',
    standard: 'Standart',
    scientific: 'Bilimsel',
    errors: {
      syntax: 'Geçersiz ifade',
      divZero: 'Sıfıra bölünemez',
      domain: 'Tanım dışı',
      overflow: 'Taşma',
    },
    sci: {
      angleLabel: { deg: 'Derece', rad: 'Radyan', grad: 'Gradyan' },
      angle: (unit: string) => `Açı birimi: ${unit}. Değiştirmek için basın`,
      second: 'İkinci işlevler',
      hyper: 'Hiperbolik işlevler',
      memoryClear: 'Belleği temizle',
      memoryRecall: 'Bellekten çağır',
      memoryAdd: 'Belleğe ekle',
      memorySubtract: 'Bellekten çıkar',
      memoryIndicator: 'Bellekte değer var',
      ans: 'Önceki sonuç',
      pi: 'Pi sayısı',
      euler: 'Euler sayısı',
      open: 'Parantez aç',
      close: 'Parantez kapat',
      sin: 'Sinüs',
      cos: 'Kosinüs',
      tan: 'Tanjant',
      asin: 'Ters sinüs',
      acos: 'Ters kosinüs',
      atan: 'Ters tanjant',
      sinh: 'Hiperbolik sinüs',
      cosh: 'Hiperbolik kosinüs',
      tanh: 'Hiperbolik tanjant',
      asinh: 'Ters hiperbolik sinüs',
      acosh: 'Ters hiperbolik kosinüs',
      atanh: 'Ters hiperbolik tanjant',
      ln: 'Doğal logaritma',
      log: 'On tabanında logaritma',
      exp: 'e üzeri',
      tenPower: 'On üzeri',
      twoPower: 'İki üzeri',
      square: 'Kare',
      cube: 'Küp',
      power: 'Üs alma',
      sqrt: 'Karekök',
      cbrt: 'Küpkök',
      root: 'Köklü ifade: y. dereceden kök',
      reciprocal: 'Çarpmaya göre ters',
      factorial: 'Faktöriyel',
      abs: 'Mutlak değer',
      mod: 'Mod',
      exponent: 'Bilimsel gösterim, on üzeri',
      percent: 'Yüzde',
    },
  },
  calendar: {
    prev: 'Önceki ay',
    next: 'Sonraki ay',
    today: 'Bugün',
    noEvents: 'Bu gün için etkinlik yok.',
    titlePlaceholder: 'Etkinlik başlığı…',
    timeLabel: 'Saat',
    add: 'Ekle',
    allDay: 'Tüm gün',
    remove: (title: string) => `“${title}” etkinliğini sil`,
    dayHasEvents: (count: number) => `${count} etkinlik`,
  },
  files: {
    root: 'Dosyalarım',
    newFolder: 'Yeni klasör',
    newFolderName: 'Yeni klasör',
    upload: 'Yükle',
    name: 'Ad',
    size: 'Boyut',
    modified: 'Eklenme',
    empty: "Bu klasör boş. Dosyaları buraya sürükleyin ya da Yükle'ye basın.",
    dropHere: 'Yüklemek için bırakın',
    uploading: (count: number) => `${count} dosya yükleniyor`,
    rename: 'Yeniden adlandır',
    download: 'İndir',
    open: 'Aç',
    openInViewer: 'Görüntüleyicide aç',
    confirmDelete: (name: string) => `“${name}” silinsin mi?`,
    confirmDeleteFolder: 'Klasörün içindeki her şey de silinir. Bu işlem geri alınamaz.',
    confirmDeleteFile: 'Bu işlem geri alınamaz.',
    items: (count: number) => `${count} öğe`,
    folder: 'Klasör',
    actionsHeader: 'İşlemler',
  },
  picker: {
    search: 'Ada göre ara',
    noMatch: 'Bu adla eşleşen dosya yok.',
    empty: "Dosyalarım'da bu türde dosya yok.",
  },
  saveAs: {
    name: 'Dosya adı',
    location: "Dosyalarım'ın ana klasörüne kaydedilir.",
    locationBeside: 'Açık dosyayla aynı klasöre kaydedilir.',
    save: 'Kaydet',
  },
  pdf: {
    open: 'PDF aç',
    fromDevice: 'Cihazdan…',
    fromFiles: "Dosyalarım'dan…",
    emptyTitle: 'Bir PDF açın',
    emptyBody: "Cihazınızdan seçin ya da buraya sürükleyin; dosya Dosyalarım'a kaydedilir.",
    dropHere: 'Açmak için bırakın',
    recent: 'Son PDF’ler',
    noRecent: "Dosyalarım'da henüz PDF yok.",
    pickerTitle: "Dosyalarım'dan PDF seç",
    search: 'Ada göre ara',
    noMatch: 'Bu adla eşleşen PDF yok.',
    uploading: 'Dosyalarım’a yükleniyor…',
    loading: 'Belge açılıyor…',
    notPdf: 'Bu dosya bir PDF değil ya da bozuk.',
    missing: 'Bu belge artık Dosyalarım’da yok. Başka bir PDF açın.',
    passwordTitle: 'Bu PDF şifreli',
    passwordBody: 'Açmak için belgenin şifresini girin.',
    passwordWrong: 'Şifre yanlış. Tekrar deneyin.',
    passwordLabel: 'Belge şifresi',
    unlock: 'Kilidi aç',
    prevPage: 'Önceki sayfa',
    nextPage: 'Sonraki sayfa',
    pageLabel: 'Sayfa numarası',
    pageOf: (total: number) => `/ ${total}`,
    zoomIn: 'Yakınlaştır',
    zoomOut: 'Uzaklaştır',
    zoomLevel: 'Yakınlaştırma düzeyi',
    fitWidth: 'Genişliğe sığdır',
    fitPage: 'Sayfaya sığdır',
    rotate: 'Sağa döndür',
    download: 'İndir',
    close: 'Belgeyi kapat',
    page: (n: number) => `Sayfa ${n}`,
    pageCount: (n: number) => `${n} sayfa`,
  },
} as const;
